import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import { RadarChart, PolarGrid, PolarAngleAxis, Radar, ResponsiveContainer, Legend } from 'recharts';
import { Loader2, Compass, ArrowRight, Download, Share2 } from 'lucide-react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DOMAIN_LABELS = {
  en: ['Physical Health', 'Strength', 'Energy', 'Space', 'Work', 'Money', 'Love', 'Friends', 'Emotional', 'Discipline', 'Learning', 'Meaning'],
  tr: ['Fiziksel Sağlık', 'Güç', 'Enerji', 'Alan', 'İş', 'Para', 'Aşk', 'Arkadaşlar', 'Duygusal', 'Disiplin', 'Öğrenme', 'Anlam'],
};

export const SharedSunMap = () => {
  const { shareId } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mapData, setMapData] = useState(null);

  useEffect(() => {
    const fetchSharedMap = async () => {
      try {
        console.log('Fetching shared map:', shareId, 'from', API_URL);
        const response = await axios.get(`${API_URL}/api/shared/${shareId}`);
        console.log('Shared map response:', response.data);
        setMapData(response.data);
        
        // Update page meta tags for social sharing
        const { user_name, has_image } = response.data;
        document.title = `${user_name}'s Sun Map - IN Inner Navigation`;
        
        // Update or create meta tags
        const updateMeta = (property, content) => {
          let meta = document.querySelector(`meta[property="${property}"]`);
          if (!meta) {
            meta = document.createElement('meta');
            meta.setAttribute('property', property);
            document.head.appendChild(meta);
          }
          meta.setAttribute('content', content);
        };
        
        updateMeta('og:title', `${user_name}'s Sun Map`);
        updateMeta('og:description', 'Check out my Sun Map from IN - Inner Navigation. Create your own life map!');
        updateMeta('og:type', 'website');
        updateMeta('og:url', window.location.href);
        if (has_image) {
          updateMeta('og:image', `${API_URL}/api/shared/${shareId}/image`);
        }
      } catch (err) {
        console.error('Error fetching shared map:', err);
        setError(language === 'tr' ? 'Paylaşılan harita bulunamadı' : 'Shared map not found');
      } finally {
        setLoading(false);
      }
    };

    if (shareId) {
      fetchSharedMap();
    } else {
      setError(language === 'tr' ? 'Geçersiz bağlantı' : 'Invalid link');
      setLoading(false);
    }
  }, [shareId, language]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  if (error || !mapData) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6 text-center">
        <Compass className="w-16 h-16 text-[#D4AF37]/50 mb-4" />
        <h1 className="font-serif text-2xl text-[#F8F5F2] mb-2">
          {error || (language === 'tr' ? 'Harita bulunamadı' : 'Map not found')}
        </h1>
        <p className="font-sans text-[#A1A1AA] mb-6">
          {language === 'tr' ? 'Bu bağlantı geçersiz veya süresi dolmuş olabilir.' : 'This link may be invalid or expired.'}
        </p>
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 px-6 py-3 rounded-full border border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#030305] transition-all font-sans"
        >
          {language === 'tr' ? 'Kendi haritanı oluştur' : 'Create your own map'}
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    );
  }

  const { user_name, scores, has_image } = mapData;
  const labels = DOMAIN_LABELS[language] || DOMAIN_LABELS.en;
  const imageUrl = `${API_URL}/api/shared/${shareId}/image`;

  const downloadImage = async () => {
    if (has_image) {
      try {
        const response = await fetch(imageUrl);
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${user_name}-sun-map.png`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success(language === 'tr' ? 'İndirildi!' : 'Downloaded!');
      } catch (err) {
        toast.error(language === 'tr' ? 'İndirme başarısız' : 'Download failed');
      }
    }
  };
  
  const data = [
    { domain: labels[0], current: scores.physical_health, ideal: 10 },
    { domain: labels[1], current: scores.strength_fitness, ideal: 10 },
    { domain: labels[2], current: scores.energy, ideal: 10 },
    { domain: labels[3], current: scores.environment_order, ideal: 10 },
    { domain: labels[4], current: scores.work_mission, ideal: 10 },
    { domain: labels[5], current: scores.money_financial, ideal: 10 },
    { domain: labels[6], current: scores.love_relationship, ideal: 10 },
    { domain: labels[7], current: scores.friends_community, ideal: 10 },
    { domain: labels[8], current: scores.emotional_stability, ideal: 10 },
    { domain: labels[9], current: scores.discipline_consistency, ideal: 10 },
    { domain: labels[10], current: scores.learning_growth, ideal: 10 },
    { domain: labels[11], current: scores.meaning_purpose, ideal: 10 },
  ];

  return (
    <div className="min-h-screen py-12 px-6 relative z-10">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="w-16 h-16 rounded-full bg-[#D4AF37]/20 flex items-center justify-center mx-auto mb-4">
            <Compass className="w-8 h-8 text-[#D4AF37]" />
          </div>
          <h1 className="font-serif font-light text-3xl text-[#F8F5F2] mb-2">
            {user_name}{language === 'tr' ? "'ın Güneş Haritası" : "'s Sun Map"}
          </h1>
          <p className="font-sans text-[#A1A1AA]">
            {language === 'tr' ? 'IN - İç Navigasyon ile oluşturuldu' : 'Created with IN — Inner Navigation'}
          </p>
        </motion.div>

        {/* Sun Map - Show image if available, otherwise render chart */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2 }}
          className="glass-panel rounded-2xl p-6 mb-8"
        >
          {has_image ? (
            <div className="flex flex-col items-center">
              <img 
                src={imageUrl} 
                alt={`${user_name}'s Sun Map`}
                className="max-w-full h-auto rounded-lg"
              />
              <button
                onClick={downloadImage}
                className="mt-4 flex items-center gap-2 px-4 py-2 rounded-full border border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-all text-sm font-sans"
              >
                <Download className="w-4 h-4" />
                {language === 'tr' ? 'Resmi İndir' : 'Download Image'}
              </button>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={400}>
              <RadarChart data={data} cx="50%" cy="50%" outerRadius="65%">
                <PolarGrid stroke="rgba(212, 175, 55, 0.2)" />
                <PolarAngleAxis
                  dataKey="domain"
                  tick={{ fill: '#D4AF37', fontSize: 10, fontFamily: 'Outfit' }}
                  tickLine={false}
                />
                <Radar
                  name={language === 'tr' ? 'Güneş İdeali' : 'Solar Ideal'}
                  dataKey="ideal"
                  stroke="#FF8C42"
                  fill="#FF8C42"
                  fillOpacity={0.08}
                  strokeWidth={3}
                />
                <Radar
                  name={language === 'tr' ? 'Mevcut Örüntü' : 'Current Pattern'}
                  dataKey="current"
                  stroke="#D4AF37"
                  fill="#D4AF37"
                  fillOpacity={0.5}
                  strokeWidth={2.5}
                />
                <Legend
                  wrapperStyle={{
                    paddingTop: '20px',
                    fontFamily: 'Outfit',
                    fontSize: '12px',
                  }}
                  iconType="circle"
                />
              </RadarChart>
            </ResponsiveContainer>
          )}
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="text-center"
        >
          <p className="font-sans text-[#A1A1AA] mb-4">
            {language === 'tr' 
              ? 'Kendi hayat haritanı keşfetmek ister misin?' 
              : 'Want to discover your own life map?'}
          </p>
          <button
            onClick={() => navigate('/auth')}
            className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[#D4AF37] text-[#030305] font-sans font-semibold hover:bg-[#D4AF37]/90 transition-all"
            data-testid="create-map-cta"
          >
            {language === 'tr' ? 'Kendi Haritanı Oluştur' : 'Create Your Own Map'}
            <ArrowRight className="w-5 h-5" />
          </button>
        </motion.div>
      </div>
    </div>
  );
};

export default SharedSunMap;
